import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import ResizeDetector from "react-resize-detector";
import { Box, Link, Stack } from "@mui/material";
import remarkGfm from "remark-gfm";

interface MarkdownCollapseProps {
  autoExpand?: boolean | null;
  editable?: boolean;
  fontSize?: number;
  maxLines?: number;
  minHeight?: number;
  placeholder?: string;
  value: string;
}

const MarkdownCollapse = ({
  autoExpand = null,
  editable = false,
  fontSize = 14,
  maxLines = 3,
  minHeight = 36,
  placeholder = "Description",
  value,
}: MarkdownCollapseProps) => {
  const [expanded, setExpanded] = useState<null | boolean>(autoExpand);

  useEffect(() => {
    setExpanded(autoExpand);
  }, [autoExpand]);

  return (
    <Box sx={{ position: "relative", minHeight }}>
      <ResizeDetector handleHeight>
        {({ height, targetRef }) => {
          const hasContent = value && value.trim().length > 0;
          const showToggle =
            hasContent &&
            (expanded !== null || Number(targetRef?.current?.scrollHeight) > Number(height));

          return (
            <Stack
              sx={{
                alignItems: "flex-start",
                gap: 0.5,
                justifyContent: "center",
                minHeight: "inherit",
                position: "relative",
              }}
            >
              <Box
                ref={targetRef}
                sx={{
                  display: "-webkit-box",
                  fontSize: fontSize,
                  overflow: "hidden",
                  pr: editable ? 5 : undefined,
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: expanded ? "unset" : maxLines,
                }}
              >
                <Box
                  className="prose prose-sm max-w-none"
                  sx={{
                    img: {
                      width: "100%",
                    },
                    "> :first-child": {
                      mt: 0,
                      pt: 0,
                    },
                    "> :last-child": {
                      mb: 0,
                      pb: 0,
                    },
                    "&:empty": {
                      "&::before": {
                        content: `"${placeholder}"`,
                        opacity: 0.5,
                      },
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
                </Box>
              </Box>
              {showToggle && (
                <Link
                  component="button"
                  type="button"
                  fontSize={14}
                  underline="hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                >
                  {expanded ? "less" : "more"}
                </Link>
              )}
            </Stack>
          );
        }}
      </ResizeDetector>
    </Box>
  );
};

export default MarkdownCollapse;
